import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, UserCheck, Upload, X, Save } from 'lucide-react';
import { Technician } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const EditTechnicianPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [technician, setTechnician] = useState<Technician | null>(null);
  
  // Form state
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [experience, setExperience] = useState('');
  const [status, setStatus] = useState('Active');
  
  // Image state
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');

  useEffect(() => {
    if (id) {
      loadTechnician(parseInt(id));
    }
  }, [id]);

  const loadTechnician = async (technicianId: number) => {
    try {
      setIsLoading(true);
      const response = await apiService.getTechnician(technicianId);
      if (response.isSuccess && response.entity) {
        const tech = response.entity;
        setTechnician(tech);
        setName(tech.name);
        setMobile(tech.mobile);
        setEmail(tech.email);
        setSpecialization(tech.specialization);
        setExperience(tech.experience.toString());
        setStatus(tech.status);
        
        if (tech.imagePath) {
          setPhotoPreview(tech.imagePath);
        }
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to load technician');
        navigate('/technicians');
      }
    } catch (error) {
      console.error('Error loading technician:', error);
      toast.error('Failed to load technician');
      navigate('/technicians');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = (file: File) => {
    if (file) {
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        toast.error('Please select a valid image file (PNG, JPEG, JPG)');
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhoto(file);
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview('');
  };

  const validateForm = () => {
    if (!name.trim()) {
      toast.error('Please enter technician name');
      return false;
    }
    
    if (!mobile.trim()) {
      toast.error('Please enter mobile number');
      return false;
    }
    
    if (!email.trim()) {
      toast.error('Please enter email address');
      return false;
    }
    
    if (!specialization.trim()) {
      toast.error('Please enter specialization');
      return false;
    }
    
    if (!experience || parseInt(experience) <= 0) {
      toast.error('Please enter valid experience (years)');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    if (mobile.length < 10) {
      toast.error('Please enter a valid mobile number');
      return false;
    }
    
    return true;
  };

  const handleUpdateTechnician = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!technician || !validateForm()) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      
      formData.append('name', name);
      formData.append('mobile', mobile);
      formData.append('email', email);
      formData.append('specialization', specialization);
      formData.append('experience', experience);
      formData.append('status', status);
      
      if (photo) {
        formData.append('photo', photo);
      }

      const response = await apiService.updateTechnician(technician.id, formData);
      
      if (response.isSuccess) {
        toast.success('Technician updated successfully');
        navigate('/technicians');
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to update technician');
      }
    } catch (error) {
      console.error('Error updating technician:', error);
      toast.error('Error updating technician');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!technician) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Technician not found</h3>
        <button
          onClick={() => navigate('/technicians')}
          className="text-purple-600 hover:text-purple-700"
        >
          Back to Technicians
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/technicians')}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Technicians
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Edit Technician</h1>
            <p className="text-sm text-gray-500">Update technician information</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <form onSubmit={handleUpdateTechnician} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter mobile number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter email address"
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specialization *
                </label>
                <input
                  type="text"
                  value={specialization}
                  onChange={(e) => setSpecialization(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Mobile Repair, Laptop Repair"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Years of Experience *
                </label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter years of experience"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="On Leave">On Leave</option>
                </select>
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Photo
            </label>
            <div className="space-y-4">
              {photoPreview ? (
                <div className="relative inline-block">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="h-32 w-32 rounded-lg object-cover border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <label htmlFor="photo-upload" className="cursor-pointer">
                        <span className="text-purple-600 hover:text-purple-700 font-medium">
                          Click to upload
                        </span>{' '}
                        or drag and drop
                      </label>
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, JPEG up to 2MB
                    </p>
                  </div>
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0])}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/technicians')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Update Technician
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTechnicianPage; 